import { baseCountry, usesLei } from './i18n';
import { vatRateOf } from './markets';
import { t as tr } from './i18n';
import { intlLocale } from './i18n';
import { advanceText, daysWithText } from './conditions';
import { fmtNum, roDateTime } from './format';
import { billedDays, currencySign, money, MONTH_DAYS } from './package';
import { deliveryLine } from './sites';
import { rfqParts, type SplitPart } from './split';
import { billedQty, termsOf } from './terms';
import type {
  Currency,
  Offer,
  OfferExtra,
  OfferPackage,
  Rfq,
  RfqItem,
  SendFile,
  SupplierRef,
  Transport,
  UserRef,
} from './types';

/**
 * Comanda de achiziție: pasul de după „Cu cine ai rămas?”. Se completează singură
 * din cerere și din oferta aleasă, omul o corectează (ce s-a negociat) și o emite.
 * Emisă, nu se mai schimbă: se anulează și se reface, cu număr nou.
 */

/** Cota standard de TVA din România (din 1 august 2025). Se poate schimba pe comandă. */
export const VAT_RATE = 21;

/** O poziție din comandă. Prețul e în moneda comenzii, fără TVA. */
export interface PoLine {
  desc: string;
  qty: number;
  unit: string;
  unitPrice: number;
  /**
   * poziție de chirie: costul real se socotește din avize (livrare → retur), la
   * tariful pe zi al poziției (`unit` 'zi', sau 'lună' = 30 de zile), cu chiria
   * minimă. Lipsă = vânzare.
   */
  rent?: { minDays: number | null };
  /** prețul din oferta aleasă, când poziția vine din ea: arată dacă s-a comandat la alt preț */
  offerPrice?: number;
}

/** 'order' = comanda din cerere; 'supplement' = suplimentare către același furnizor, fără concurență. */
export type PoKind = 'order' | 'supplement';

/** Ce se poate corecta înainte de emitere. Sumele sunt în moneda comenzii. */
export interface PoDraft {
  supplierCui: string;
  supplierAddress: string;
  contactName: string;
  contactEmail: string;
  deliveryAddress: string;
  /** 'AAAA-LL-ZZ' sau '' */
  deliveryDate: string;
  /** termenul din ofertă, cu cuvintele ei: „14 zile” */
  deliveryTerm: string;
  currency: Currency;
  /** lei pentru 1 EUR; null la RON */
  fxRate: number | null;
  lines: PoLine[];
  /** cum s-a convenit transportul: inclus, separat (cu `transportCost`) sau nespus */
  transport: Transport;
  transportCost: number;
  extras: OfferExtra[];
  vatRate: number;
  payment: string;
  notes: string;
}

/** Cumpărătorul, cum apare pe document. */
export interface PoBuyer {
  name: string;
  cui: string;
  regCom: string;
  address: string;
  signature: string;
}

/** Tot documentul, cum s-a emis. Se păstrează ca atare. */
export interface PurchaseOrderData extends PoDraft {
  buyer: PoBuyer;
  supplierName: string;
  siteName: string;
  rfqTitle: string;
}

/**
 * 'pending' = așteaptă aprobare (nu e încă emisă, nu pleacă la furnizor);
 * 'rejected' = respinsă la aprobare, rămâne pentru istoric.
 */
export type PoStatus = 'issued' | 'canceled' | 'pending' | 'rejected';

/** Un pas de aprobare al comenzii, cu decizia lui. */
export interface OrderApproval {
  step: number;
  /** pragul pasului, în lei fără TVA */
  minValue: number;
  /** cine trebuie să aprobe; null = oricare aprobator al firmei */
  approver: UserRef | null;
  decision: 'approved' | 'rejected' | null;
  decidedBy: UserRef | null;
  comment: string;
  /** ISO 8601 */
  decidedAt: string | null;
}

export interface PurchaseOrder {
  id: string;
  rfqId: string;
  number: string;
  /** lipsă = 'order' (comenzile de dinainte de suplimentări) */
  kind?: PoKind;
  status: PoStatus;
  /** ISO 8601: marcată „livrată integral” (la chirie, „returnată integral”); null = în desfășurare */
  completedAt?: string | null;
  /** ISO 8601: când a plecat la furnizor; null = încă nu */
  sentAt?: string | null;
  /** 'procumi' = mailul din aplicație; 'manual' = omul a trimis-o el; '' = încă nu */
  sentVia?: '' | 'procumi' | 'manual';
  /** ultima eroare la trimiterea din Procumi; '' = fără */
  mailError?: string;
  supplier: SupplierRef;
  /** ISO 8601 */
  issuedAt: string;
  canceledAt: string | null;
  cancelReason: string;
  createdBy: UserRef | null;
  /** totalul fără TVA, în lei */
  netLei: number;
  data: PurchaseOrderData;
  /** livrările și retururile, cu avizele lor; cea mai veche prima */
  deliveries: Delivery[];
  /** pașii de aprobare; gol = emisă direct */
  approvals?: OrderApproval[];
}

/** 'in' = marfa a venit; 'return' = a plecat înapoi (cofraje, schele, marfă refuzată). */
export type DeliveryKind = 'in' | 'return';

/** O livrare pe comandă, cu avizul de însoțire. */
export interface Delivery {
  id: string;
  kind: DeliveryKind;
  /** 'AAAA-LL-ZZ', ziua de pe aviz */
  date: string;
  /** numărul avizului; '' dacă nu s-a trecut */
  docNumber: string;
  note: string;
  createdBy: UserRef | null;
  /** ISO 8601 */
  createdAt: string;
  /** ISO 8601: verificată de Achiziții; null = trecută de pe șantier, de confirmat */
  confirmedAt: string | null;
  confirmedBy: UserRef | null;
  /** fotografiile sau PDF-urile avizului */
  files: SendFile[];
}

export interface DeliveryInput {
  kind: DeliveryKind;
  date: string;
  docNumber: string;
  note: string;
}

/**
 * Returul are sens doar la chirie (cofraje, schele): fierul cumpărat nu se
 * mai întoarce. Cererea are chirie dacă măcar o poziție are durata cerută.
 */
export const hasRental = (rfq: { items: { id: string; rentDays?: number | null }[] }): boolean =>
  rfq.items.some((it) => it.rentDays != null);

/**
 * Comanda e numai chirie: se încheie când se returnează tot. Una mixtă (beton +
 * pompă) se încheie ca orice cumpărare, „livrată integral”; returul pompei se
 * trece tot pe aviz.
 */
export const allRental = (order: { lines: PoLine[] }): boolean =>
  order.lines.length > 0 && order.lines.every((l) => l.rent != null);

/**
 * Ziua din care comanda contează ca „cumpărat”, 'AAAA-LL-ZZ': prima livrare de
 * pe aviz, altfel data dorită de pe comandă. null = nu se știe încă, deci
 * comanda rămâne „angajată”. Nu ziua emiterii: o comandă dată azi pentru marfă
 * care vine luna viitoare nu e o cheltuială a lunii acesteia.
 *
 * Cu `today`: o comandă fără aviz a cărei dată dorită a trecut **nu** e
 * cumpărată — marfa n-a venit, iar panoul o arată ca livrare întârziată; ar fi
 * contradictoriu să fie și „achiziționată”. Marcată „livrată integral” fără
 * aviz, contează din data dorită.
 */
export function orderCostDay(
  o: {
    data: Pick<PoDraft, 'deliveryDate'>;
    deliveries?: Pick<Delivery, 'kind' | 'date'>[];
    completedAt?: string | null;
  },
  today?: string,
): string | null {
  const first = (o.deliveries ?? [])
    .filter((d) => d.kind === 'in')
    .map((d) => d.date)
    .sort()[0];
  if (first) return first;
  const wanted = o.data.deliveryDate || null;
  if (!wanted || !today || o.completedAt || wanted > today) return wanted;
  return null;
}

/** „Livrare · aviz 4521”, „Retur, fără număr de aviz”. */
export function deliveryTitle(d: Pick<Delivery, 'kind' | 'docNumber'>): string {
  const what = d.kind === 'return' ? tr('Retur') : tr('Livrare');
  return d.docNumber ? tr('{0} · aviz {1}', what, d.docNumber) : tr('{0}, fără număr de aviz', what);
}

/** Rezumatul de lângă comandă: „2 livrări · 1 retur”; '' fără nimic trecut. */
export function deliverySummary(list: Pick<Delivery, 'kind'>[]): string {
  const ins = list.filter((d) => d.kind === 'in').length;
  const back = list.length - ins;
  const parts: string[] = [];
  if (ins) parts.push(ins === 1 ? tr('1 livrare') : tr('{0} livrări', ins));
  if (back) parts.push(back === 1 ? tr('1 retur') : tr('{0} retururi', back));
  return parts.join(' · ');
}

/** Ce se trimite la emitere: ciorna, plus datele firmei dacă le-ai completat acum. */
export type PoInput = PoDraft & {
  kind?: PoKind;
  /** la comanda împărțită: furnizorul părții; lipsă = furnizorul ales */
  supplierId?: string;
};

export interface PoTotals {
  /** valoarea pozițiilor */
  lines: number;
  /** transport + alte costuri */
  extra: number;
  net: number;
  vat: number;
  gross: number;
}

const cents = (n: number) => Math.round(n * 100) / 100;

/** Valoarea unei poziții, rotunjită la bani, cum apare pe document. */
export const lineValue = (l: PoLine): number => cents(l.qty * l.unitPrice);

/**
 * Totalurile documentului. Fiecare rând se rotunjește la bani înainte de
 * adunare, ca suma de jos să fie suma rândurilor de deasupra, la ban.
 */
export function poTotals(
  d: Pick<PoDraft, 'lines' | 'transport' | 'transportCost' | 'extras' | 'vatRate'>,
): PoTotals {
  const lines = cents(d.lines.reduce((n, l) => n + lineValue(l), 0));
  const transport = d.transport === 'extra' ? cents(d.transportCost) : 0;
  const extra = cents(transport + d.extras.reduce((n, e) => n + cents(e.amount), 0));
  const net = cents(lines + extra);
  const vat = cents((net * d.vatRate) / 100);
  return { lines, extra, net, vat, gross: cents(net + vat) };
}

/** Totalul fără TVA, în lei (pentru registrul șantierului și panou). */
export const netInLei = (d: PoDraft): number =>
  cents(poTotals(d).net * (d.currency === 'EUR' ? (d.fxRate ?? 1) : 1));

/** Formele juridice nu intră în inițiale: „Construct Demo SRL” → CD, nu CDS. */
const LEGAL = new Set(['srl', 'sa', 'srld', 'pfa', 'ii', 'if', 'snc', 'scs', 'sca', 'ra', 'scm', 'ong']);

/** Inițialele firmei pentru seria comenzilor: „Construct Demo SRL” → „CD”. */
export function companyInitials(name: string): string {
  const words = name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[^A-Za-z0-9]+/)
    .filter((w) => w && !LEGAL.has(w.toLowerCase()));
  if (!words.length) return 'PC';
  // Un singur cuvânt: primele două litere, ca seria să nu fie o literă singură.
  const initials = words.length === 1 ? words[0]!.slice(0, 2) : words.map((w) => w[0]).join('');
  return initials.slice(0, 4).toUpperCase();
}

/** „CD-2026-0001” */
export const poNumber = (prefix: string, year: number, seq: number): string =>
  `${prefix}-${year}-${String(seq).padStart(4, '0')}`;

/** Comanda în vigoare a unei cereri (emisă, neanulată), fără suplimentări, dacă există. */
export const activeOrder = (rfq: Pick<Rfq, 'orders'>): PurchaseOrder | null =>
  rfq.orders?.find((o) => o.status === 'issued' && o.kind !== 'supplement') ?? null;

/** Suplimentările în vigoare ale unei cereri, cea mai veche prima. */
export const activeSupplements = (rfq: Pick<Rfq, 'orders'>): PurchaseOrder[] =>
  (rfq.orders ?? [])
    .filter((o) => o.status === 'issued' && o.kind === 'supplement')
    .sort((a, b) => (a.issuedAt < b.issuedAt ? -1 : 1));

/** Toate comenzile în vigoare: comanda și suplimentările ei. */
export const issuedOrders = (rfq: Pick<Rfq, 'orders'>): PurchaseOrder[] =>
  (rfq.orders ?? []).filter((o) => o.status === 'issued');

/**
 * Oferta pe set intră în comandă cum a scris-o furnizorul, nu împărțită la
 * cantitatea cerută (53 €/zi pentru 501 mp, nu 4,77 €/mp — preț pe care nu l-a
 * dat nimeni). Chiria: tariful × zilele socotite (cu chiria minimă), iar un
 * tarif lunar rămâne lunar; vânzarea: un set. Costurile o singură dată, separat.
 */
function packageLines(pkg: OfferPackage, it: RfqItem): PoLine[] {
  const area = `${fmtNum(pkg.coveredQty ?? it.qty)} ${it.unit}`;
  const lines: PoLine[] = [];
  if (pkg.periodDays) {
    const days = billedDays(pkg, it.rentDays ?? null);
    const monthly = pkg.periodDays === MONTH_DAYS;
    lines.push({
      desc: tr('{0} — chirie {1}', it.desc, area),
      qty: monthly ? Math.round((days / MONTH_DAYS) * 10_000) / 10_000 : days,
      unit: monthly ? 'lună' : 'zi',
      unitPrice: monthly ? pkg.total : Math.round((pkg.total / pkg.periodDays) * 10_000) / 10_000,
      // ce s-a comandat e o estimare pe zilele cerute; costul real vine din avize
      rent: { minDays: pkg.minDays },
    });
  } else {
    lines.push({
      desc: tr('{0} — set complet, {1}', it.desc, area),
      qty: 1,
      unit: 'set',
      unitPrice: pkg.total,
    });
  }
  if (pkg.oneTime) {
    lines.push({
      desc: tr('{0} — costuri o singură dată', it.desc),
      qty: 1,
      unit: 'buc',
      unitPrice: pkg.oneTime,
    });
  }
  return lines;
}

/**
 * Ciorna comenzii, completată din cerere și din oferta furnizorului ales.
 * Cantitățile sunt cele plătite (cu comanda minimă și ambalajul), prețurile în
 * moneda ofertei. La comanda împărțită, `supplierId` alege partea: doar
 * pozițiile ei, din oferta furnizorului ei. null dacă nu s-a ales furnizorul
 * sau furnizorul nu e o parte a alegerii.
 */
export function poDraftFromRfq(rfq: Rfq, supplierId?: string): PoDraft | null {
  const part = partOf(rfq, supplierId);
  if (!part) return null;
  const send = rfq.sends.find((s) => s.supplier.id === part.supplier.id);
  const offer = send?.offer ?? null;
  const currency: Currency = offer?.currency === 'EUR' ? 'EUR' : 'RON';
  const fx = currency === 'EUR' ? (offer?.fxRate ?? null) : null;

  const lines: PoLine[] = itemsOf(rfq, part).flatMap((it) => {
    const pkg = offer?.packages?.[it.id];
    if (pkg) return packageLines(pkg, it);
    const quoted = currency === 'EUR' ? offer?.quoted?.[it.id] : offer?.prices[it.id];
    return [
      {
        desc: it.desc,
        qty: offer ? billedQty(it.qty, termsOf(offer, it)) : it.qty,
        unit: it.unit,
        unitPrice: quoted ?? 0,
        ...(quoted != null ? { offerPrice: quoted } : {}),
      },
    ];
  });

  return {
    supplierCui: '',
    supplierAddress: '',
    // persoana căreia i-a plecat cererea: ea a făcut oferta
    contactName: send?.contact.name ?? '',
    contactEmail: send?.contact.email ?? '',
    deliveryAddress: deliveryLine(rfq.site),
    // ziua în care trebuie pe șantier, din cerere; omul o poate muta
    deliveryDate: rfq.neededBy ?? '',
    // oferta prin link dă termenul doar în zile
    deliveryTerm: offer ? daysWithText(offer.deliveryDays, offer.delivery, offer.deliveryWorking) : '',
    currency,
    fxRate: fx,
    lines,
    transport: offer?.transport ?? '',
    transportCost: offer?.transport === 'extra' ? (offer.transportCost ?? 0) : 0,
    extras: offer?.extras ?? [],
    // cota standard a țării firmei
    vatRate: vatRateOf(baseCountry()),
    payment: offer ? paymentTerms(offer) : '',
    notes: '',
  };
}

/** Partea cererii pentru furnizor (implicit, furnizorul ales); null dacă nu e una. */
function partOf(rfq: Rfq, supplierId?: string): SplitPart | null {
  const parts = rfqParts(rfq);
  return supplierId ? (parts.find((p) => p.supplier.id === supplierId) ?? null) : (parts[0] ?? null);
}

/** Pozițiile părții, în ordinea din cerere. */
const itemsOf = (rfq: Rfq, part: SplitPart): RfqItem[] =>
  rfq.items.filter((it) => part.itemIds.includes(it.id));

/** Plata pe comandă: avansul și termenul, cu textul ofertei. „30% avans · 30 zile”. */
function paymentTerms(offer: Offer): string {
  const parts = [advanceText(offer.advance, offer), daysWithText(offer.paymentDays, offer.payment)];
  return parts.filter(Boolean).join(' · ').slice(0, 200);
}

/**
 * Ciorna unei suplimentări: același furnizor, pe proiectul la care l-ai ales.
 * Pozițiile din oferta lui vin cu prețul ofertei (deja negociat), dar fără
 * cantitate — omul scrie cât mai are nevoie și scoate ce nu-i trebuie. La
 * chirie, tariful pe zi pentru cantitatea în plus îl scrie omul: oferta e pe
 * set, nu pe mp. Poziții noi se adaugă cu preț scris de mână.
 */
export function supplementDraft(rfq: Rfq, supplierId?: string): PoDraft | null {
  const base = poDraftFromRfq(rfq, supplierId);
  const part = partOf(rfq, supplierId);
  if (!base || !part) return null;
  const offer = rfq.sends.find((s) => s.supplier.id === part.supplier.id)?.offer ?? null;
  const lines: PoLine[] = itemsOf(rfq, part).map((it) => {
    const pkg = offer?.packages?.[it.id];
    if (pkg?.periodDays) {
      return {
        desc: tr('{0} — suplimentare chirie', it.desc),
        qty: 0,
        unit: 'zi',
        unitPrice: 0,
        rent: { minDays: pkg.minDays },
      };
    }
    if (pkg) return { desc: it.desc, qty: 0, unit: it.unit, unitPrice: 0 };
    const quoted = base.currency === 'EUR' ? offer?.quoted?.[it.id] : offer?.prices[it.id];
    return {
      desc: it.desc,
      qty: 0,
      unit: it.unit,
      unitPrice: quoted ?? 0,
      ...(quoted != null ? { offerPrice: quoted } : {}),
    };
  });
  return { ...base, lines, transportCost: 0, extras: [], deliveryDate: '' };
}

/** Pozițiile comandate la alt preț decât cel din ofertă: „30 lei în ofertă, 32 pe comandă”. */
export function priceDrift(lines: PoLine[]): { line: PoLine; pct: number }[] {
  return lines
    .filter((l) => l.offerPrice != null && l.offerPrice > 0 && Math.abs(l.unitPrice - l.offerPrice) >= 0.005)
    .map((l) => ({ line: l, pct: Math.round(((l.unitPrice - l.offerPrice!) / l.offerPrice!) * 1000) / 10 }));
}

/** Ciorna refăcută dintr-o comandă anulată: păstrează ce corectase omul. */
export function poDraftFromOrder(o: PurchaseOrder): PoDraft {
  const { buyer: _b, supplierName: _s, siteName: _n, rfqTitle: _t, ...draft } = o.data;
  return draft;
}

/** Suma cu moneda comenzii: „1.234,50 lei”, „1.234,50 €”. */
export function poMoney(n: number, c: Currency): string {
  if (!usesLei()) return money(n, c);
  const s = n.toLocaleString(intlLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${s} ${currencySign(c)}`;
}

/** Comanda ca TSV, pentru lipit în Excel. Numerele cu virgulă, ca în Excel-ul românesc. */
export function poTsv(o: Pick<PurchaseOrder, 'number' | 'issuedAt' | 'data'>): string {
  const d = o.data;
  const t = poTotals(d);
  const num = (n: number) => String(cents(n)).replace('.', ',');
  const sign = currencySign(d.currency);
  const rows: string[][] = [
    [tr('Comandă {0}', o.number), roDateTime(o.issuedAt)],
    [tr('Furnizor'), d.supplierName, d.supplierCui],
    [tr('Livrare'), d.deliveryAddress],
    [],
    ['Nr.', tr('Denumire'), 'UM', tr('Cantitate'), tr('Preț unitar ({0})', sign), tr('Valoare ({0})', sign)],
    ...d.lines.map((l, i) => [
      String(i + 1),
      l.desc,
      l.unit,
      num(l.qty),
      num(l.unitPrice),
      num(lineValue(l)),
    ]),
    [],
  ];
  if (d.transport === 'extra') rows.push(['', tr('Transport'), '', '', '', num(d.transportCost)]);
  for (const e of d.extras) rows.push(['', e.label, '', '', '', num(e.amount)]);
  rows.push(
    ['', tr('Total fără TVA'), '', '', '', num(t.net)],
    ['', tr('TVA {0}%', fmtNum(d.vatRate)), '', '', '', num(t.vat)],
    ['', tr('Total cu TVA'), '', '', '', num(t.gross)],
  );
  return rows.map((r) => r.join('\t')).join('\n');
}

/** Mailul care însoțește comanda. PDF-ul se atașează din programul de mail. */
export function poMail(o: Pick<PurchaseOrder, 'number' | 'data'>): {
  subject: string;
  body: string;
} {
  const d = o.data;
  const t = poTotals(d);
  const first = d.contactName.trim().split(' ')[0];
  const delivery = [d.deliveryDate ? `pe ${roDateTime(`${d.deliveryDate}T12:00:00`)}` : '', d.deliveryTerm]
    .filter(Boolean)
    .join(', ');
  return {
    subject: tr('Comandă {0} – {1}', o.number, d.rfqTitle),
    body: tr(
      'Bună ziua{0},\n\nVă transmitem comanda nr. {1} pentru „{2}”, conform ofertei dumneavoastră. Documentul este atașat.\n\nValoare: {3} fără TVA ({4} cu TVA)\nLoc de livrare: {5}{6}\n\nVă rugăm să ne confirmați primirea comenzii și data livrării.\n\nMulțumim,\n{7}',
      first ? `, ${first}` : '',
      o.number,
      d.rfqTitle,
      poMoney(t.net, d.currency),
      poMoney(t.gross, d.currency),
      d.deliveryAddress,
      delivery ? tr('\nLivrare: {0}', delivery) : '',
      d.buyer.signature || d.buyer.name,
    ),
  };
}

/** Comanda din cerere care așteaptă aprobare. */
export const pendingOrder = (rfq: { orders?: PurchaseOrder[] }): PurchaseOrder | null =>
  rfq.orders?.find((o) => o.status === 'pending' && o.kind !== 'supplement') ?? null;

/** Suplimentările care așteaptă aprobare. */
export const pendingSupplements = (rfq: { orders?: PurchaseOrder[] }): PurchaseOrder[] =>
  (rfq.orders ?? []).filter((o) => o.status === 'pending' && o.kind === 'supplement');

/** Pasul la care stă comanda; null dacă nu așteaptă nimic. */
export function currentApproval(o: PurchaseOrder): OrderApproval | null {
  if (o.status !== 'pending') return null;
  return o.approvals?.find((a) => !a.decision) ?? null;
}

/**
 * Poate omul ăsta să decidă pasul de acum? Doar un Aprobator; dacă pasul are
 * un om anume, doar el.
 */
export function canApprove(o: PurchaseOrder, user: { id: string; role: string }): boolean {
  const step = currentApproval(o);
  if (!step || user.role !== 'approver') return false;
  return !step.approver || step.approver.id === user.id;
}
